import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMessageTemplates, type MessageTemplateCatalogEntry } from '../api/client';
import { useAuth } from '../context/AuthContext';
import MessageTemplateEditor, { messageTemplateApiError } from './MessageTemplateEditor';

interface EditMessageTemplateModalProps {
  templateKey: string;
  values: Record<string, string>;
  onChanged: () => void;
}

export default function EditMessageTemplateModal({
  templateKey,
  values,
  onChanged,
}: EditMessageTemplateModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState<MessageTemplateCatalogEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canEdit = user?.role === 'owner' || user?.role === 'admin';

  const close = () => {
    setOpen(false);
    setEntry(null);
    setError('');
  };

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError('');
    getMessageTemplates()
      .then((response) => {
        if (!active) return;
        const selected = response.data.items.find((item) => item.key === templateKey);
        if (!selected) throw new Error(t('settings.messageTemplates.notFound', 'Template not found'));
        setEntry(selected);
      })
      .catch((requestError) => {
        if (active) setError(messageTemplateApiError(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, t, templateKey]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!canEdit) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-primary-300 bg-white px-2.5 py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
        data-testid={`edit-template-${templateKey}`}
      >
        {t('settings.messageTemplates.edit', 'Edit template')}
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-950/45 p-4" onClick={close}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`edit-template-title-${templateKey}`}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
              <div>
                <p className="text-sm font-medium text-primary-700">
                  {t('settings.messageTemplates.edit', 'Edit template')}
                </p>
                <h2 id={`edit-template-title-${templateKey}`} className="text-lg font-bold text-gray-900">
                  {entry?.label || templateKey}
                </h2>
              </div>
              <button type="button" onClick={close} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50">
                {t('common.close', 'Close')}
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              {loading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
              {entry && (
                <MessageTemplateEditor
                  entry={entry}
                  previewValues={values}
                  onChange={(updated) => {
                    setEntry(updated);
                    onChanged();
                  }}
                  onSaved={close}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
